
import Vue from 'vue'
import Broadcast from '@/types/Broadcast.interface'
import Button from '@/types/components/Button.interface'
import IListAction, { IListActionType } from '@/types/components/IListAction.interface'
import Analytics from '@/services/Analytics'
export default Vue.extend({
    methods: {
        broadcastActions(broadcast: Broadcast): IListAction {
            //  Grab our menu buttons
            const menu = this.broadcastActionsMenu(broadcast)

            //  Create our IListAction
            const action: IListAction = {
                type: IListActionType.menu,
                menu: menu
            }

            //  Return our IListAction
            return action
        },
        broadcastActionsMenu(broadcast: Broadcast, icons: boolean = false): Button[] {
            //  Get the broadcast schedule state
            const schedule = broadcast.schedule
            const recurring = schedule && schedule.recurring == 1
            const paused = schedule && schedule.paused !== null

            //  Check if the current user is an operator
            const role = this.$store.getters.userRole(this.$store.getters.user)
            const operator = role?.name === 'Operator'

            //  Assemble the action menu
            const menu: Button[] = [
                {
                    icon: 'mdi-pencil',
                    text: !icons ? 'Edit Broadcast' : null,
                    tooltip: icons ? 'Edit Broadcast' : null,
                    color: 'primary',
                    callback: () => this.goToBroadcastEdit(broadcast),
                    hidden: !schedule
                },
                {
                    icon: 'mdi-play',
                    text: !icons ? 'Resume Schedule' : null,
                    tooltip: icons ? 'Resume Schedule' : null,
                    color: 'primary',
                    callback: () => this.broadcastResume(broadcast),
                    hidden: !recurring || !paused
                },
                {
                    icon: 'mdi-pause',
                    text: !icons ? 'Pause Schedule' : null,
                    tooltip: icons ? 'Pause Schedule' : null,
                    color: 'primary',
                    callback: () => this.broadcastPause(broadcast),
                    hidden: !recurring || paused
                },
                {
                    icon: 'bi-trash-fill',
                    text: !icons ? 'Delete' : null,
                    tooltip: icons ? 'Delete' : null,
                    color: 'primary',
                    callback: () => this.broadcastDelete(broadcast),
                    hidden: operator
                },
                {
                    icon: 'mdi-content-copy',
                    text: !icons ? 'Copy' : null,
                    tooltip: icons ? 'Copy' : null,
                    color: 'primary',
                    callback: () => this.broadcastCopy(broadcast),
                    hidden: this.broadcastIsEmpty(broadcast)
                }
            ]

            //  Return the menu
            return menu
        },
        broadcastCopy(broadcast: Broadcast): void {
            //  Report to Analytics
            Analytics.track('BroadcastCopyClicked')

            //  Navigate to broadcasts create
            this.$router.push(`/broadcasts/create?copy=${broadcast.id}`)
        },
        broadcastDelete(broadcast: Broadcast): void {
            this.$store.dispatch('dialogOpen', {
                type: 'CONFIRM',
                title: 'Delete Broadcast?',
                text: `Are you sure you want to delete this broadcast?`,
                icon: 'mdi-alert',
                color: 'red darken-2',
                callback: async (result: boolean): Promise<void> => {
                    //  If the user selected 'NO', do nothing
                    if (!result) return

                    //  Else, delete the broadcast
                    const r = await this.$store.dispatch('broadcastsDelete', broadcast.id)

                    //  If the request failed, do nothing
                    if (!r) return

                    //  Check if we're in BroadcastsProfile
                    const needsNav = this.$route.path === `/broadcasts/${broadcast.id}`

                    //  If we're on the profile that was delted, nav to broadcasts index
                    if (needsNav) this.$router.push('/broadcasts')

                    //  Else, notify the user of success
                    this.$store.commit('appNotificationBarSet', {
                        message: 'Broadcast deleted'
                    })
                }
            })
        },
        broadcastIsEmpty(broadcast: Broadcast): boolean {
            //  Our broadcast is only empty if it doesn't have persons or groups
            return !broadcast.group_ids && !broadcast.person_ids
        },
        async broadcastPause(broadcast: Broadcast): Promise<void> {
            //  Get the id we're updating
            const id = broadcast.schedule.id

            //  Send the request to the API
            const response = await this.$store.dispatch('schedulesPause', id)

            //  If the request failed, do nothing
            if (!response) return

            //  Update the broadcast
            broadcast.schedule.paused = response.paused

            //  Notify use of success
            this.$store.commit('appNotificationBarSet', {
                message: 'Broadcast Paused'
            })
        },
        async broadcastResume(broadcast: Broadcast): Promise<void> {
            //  Get the id we're updating
            const id = broadcast.schedule.id

            //  Send the request to the API
            const response = await this.$store.dispatch('schedulesResume', id)

            //  If the request failed, do nothing
            if (!response) return

            //  Update the broadcast
            broadcast.schedule.paused = response.paused

            //  Notify use of success
            this.$store.commit('appNotificationBarSet', {
                message: 'Broadcast Schedule Resumed'
            })
        },
        broadcastSent(broadcast: Broadcast): number {
            //  If the sent list does NOT exist, return zero
            if (!broadcast.sent) return 0

            //  Else, set the sent total
            let total = broadcast.sent.length

            //  If the failed list exists, substrict it
            if (broadcast.failures) total = total - broadcast.failures.length

            //  Return the total
            return total
        },
        broadcastSentAt(broadcast: Broadcast): string {
            //  Set our sent date to the start date, or created at if it hasn't started sending yet
            const sent = broadcast.started_at || broadcast.created_at

            //  Grab our braodcast status
            const status = broadcast.status

            //  Grab our broadcast schedule
            const schedule = broadcast.schedule

            //  If our status is scheduled but we have no schedule, just return an empty string
            if (status === 'scheduled' && !schedule) return ''

            //  Create our object pseudo switch based on broadcast status
            const line = {
                completed: `Completed ${this.$options.filters.formattedDatetime(broadcast.completed_at)}`,
                sending: `Sent ${this.$options.filters.formattedDatetime(sent)}`,
                scheduled: `Next Send: ` + this.$options.filters.scheduleNextSendTime(schedule?.next_send)
            }[status]

            //  Return our sent at line
            return line
        },
        broadcastStatus(broadcast: Broadcast): string {
            let status = broadcast.status

            //  Check for different possible statuses
            const completed = status === 'completed'
            const recurring = broadcast.schedule && broadcast.schedule.recurring === 1
            const paused = broadcast.schedule && broadcast.schedule.paused

            //  If the broadcast is recurring, set status 'recurring'
            if (!completed && recurring) status = 'recurring'

            //  If the broadcast is paused, set status to 'paused'
            if (!completed && recurring && paused) status = 'paused'

            //  Convert the broadcast status to pascal case
            const formatted = this.$options.filters.pascalCase(status)

            //  Map the text color for the broadcast status
            const color = {
                completed: '#6142b0',
                draft: '#e7a730',
                failed: '#e53935',
                sending: '#8168C0',
                scheduled: '#E7A730',
                paused: '#757575',
                recurring: '#6142b0'
            }[status]

            //  Return html status
            return `<span style="color: ${color};">${formatted}</span>`
        },
        goToBroadcastEdit(broadcast: Broadcast): void {
            this.$router.push(`/broadcasts/${broadcast.id}/edit`)
        }
    }
})